/**
 * Header with title + Save button for Admin Settings.
 *
 * Extracted from AdminSettingsPage.tsx (Sub-PR 6.3).
 */

import { useTranslation } from 'react-i18next'

interface AdminSettingsHeaderProps {
  saving: boolean
  savedMessage: string | null
  onSave: () => void
}

function SpinnerIcon(): React.ReactElement {
  return (
    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}

function CheckIcon(): React.ReactElement {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  )
}

export function AdminSettingsHeader({
  saving,
  savedMessage,
  onSave,
}: AdminSettingsHeaderProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--text)]">
          {t('adminSettings.title')}
        </h1>
        <p className="mt-1 text-sm text-[var(--text-dim)]">{t('adminSettings.subtitle')}</p>
      </div>
      <div className="flex items-center gap-3">
        {savedMessage && (
          <span className="text-sm text-green-600 dark:text-green-400">{savedMessage}</span>
        )}
        <button
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {saving ? (
            <>
              <SpinnerIcon />
              {t('adminSettings.saving')}
            </>
          ) : (
            <>
              <CheckIcon />
              {t('adminSettings.saveChanges')}
            </>
          )}
        </button>
      </div>
    </div>
  )
}
